import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Route, Switch, useLocation } from "wouter";

import { api, type SafeUser, type SetupStatus } from "./lib/api";

import { Layout } from "./components/Layout";
import { EulaModal } from "./components/eula/EulaModal";
import { LoginPage } from "./pages/Login";
import { SetupPage } from "./pages/Setup";
import { DashboardPage } from "./pages/Dashboard";
import { SourcesPage } from "./pages/Sources";
import { SourceDetailPage } from "./pages/SourceDetail";
import { SourcePreviewPage } from "./pages/SourcePreview";
import { ConnectorsPage } from "./pages/Connectors";
import { SchedulePage } from "./pages/Schedule";
import { ArtifactsPage } from "./pages/Artifacts";
import { EndpointsPage } from "./pages/Endpoints";
import { AuditPage } from "./pages/Audit";
import { AssetDiscoveryPage } from "./pages/AssetDiscovery";
import { SettingsPage } from "./pages/Settings";
import { NotificationsPage } from "./pages/Notifications";

// App is the top-level component. Boots by fetching setup status + current
// session, then routes either to the setup wizard, the login screen, or the
// authenticated app shell.
export function App() {
  const setupQuery = useQuery({
    queryKey: ["setupStatus"],
    queryFn: () => api.get<SetupStatus>("/api/v1/setup/status"),
  });

  const meQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<SafeUser>("/api/v1/me"),
    retry: false,
    enabled: setupQuery.data?.setupComplete === true,
  });

  if (setupQuery.isLoading) return <SplashScreen message="Connecting to agent…" />;
  if (setupQuery.error)
    return <SplashScreen message="Could not reach the agent on this host." error />;

  if (!setupQuery.data?.setupComplete) {
    return <SetupPage onComplete={() => setupQuery.refetch()} />;
  }

  if (meQuery.isLoading) return <SplashScreen message="Loading session…" />;
  if (!meQuery.data) return <LoginPage onLogin={() => meQuery.refetch()} />;

  const me = meQuery.data;

  // Credentials alone are enough to sign in (internal/server/handlers.go's
  // login handler no longer blocks on this) - acceptance is gated here,
  // right after login, and enforced server-side on every other call by
  // auth.RequireEulaAccepted so this gate can't just be skipped client-side.
  if (!me.eulaAccepted) {
    return <EulaGate onResolved={() => meQuery.refetch()} />;
  }

  return <AppShell me={me} />;
}

function EulaGate({ onResolved }: Readonly<{ onResolved: () => void }>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAccept() {
    setBusy(true);
    setError(null);
    try {
      await api.post<SafeUser>("/api/v1/eula/accept");
      onResolved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record acceptance.");
      setBusy(false);
    }
  }

  async function handleDecline() {
    setBusy(true);
    try {
      await api.post("/api/v1/logout");
    } finally {
      // A full reload, not meQuery.refetch(): the refetch's own GET /api/v1/me
      // now 401s post-logout, and react-query keeps the last *successful*
      // data on a failed refetch - so a refetch alone would leave `me` (and
      // this gate) stuck showing the stale pre-logout state. Layout.tsx's
      // logout button uses the same reload for the same reason.
      window.location.href = "/";
    }
  }

  return (
    <>
      <SplashScreen
        message={error ?? "Please accept the End User License Agreement to continue."}
        error={!!error}
      />
      <EulaModal
        open
        mode="mandatory"
        onAccept={busy ? undefined : handleAccept}
        onDecline={busy ? undefined : handleDecline}
        onClose={() => {}}
      />
    </>
  );
}

function AppShell({ me }: Readonly<{ me: SafeUser }>) {
  const [location, setLocation] = useLocation();
  return (
    <Layout user={me} onLocationChange={setLocation} currentPath={location}>
      <Switch>
        <Route path="/" component={DashboardPage} />
        <Route path="/connectors" component={ConnectorsPage} />
        <Route path="/sources" component={SourcesPage} />
        <Route path="/sources/:id/preview" component={SourcePreviewPage} />
        <Route path="/sources/:id" component={SourceDetailPage} />
        <Route path="/asset-discovery" component={AssetDiscoveryPage} />
        {/* Scanner was renamed to Asset Discovery; old deep-links still work. */}
        <Route path="/scanner">
          {() => <RedirectTo to="/asset-discovery" setLocation={setLocation} />}
        </Route>
        <Route path="/schedule" component={SchedulePage} />
        <Route path="/artifacts" component={ArtifactsPage} />
        <Route path="/endpoints" component={EndpointsPage} />
        {/* Users moved into Settings; old deep-links still work. */}
        <Route path="/users">
          {() => <RedirectTo to="/settings#users" setLocation={setLocation} />}
        </Route>
        <Route path="/audit">{() => (me.role === "admin" ? <AuditPage /> : <Forbidden />)}</Route>
        <Route path="/settings">{() => <SettingsPage user={me} />}</Route>
        <Route path="/notifications" component={NotificationsPage} />
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

function SplashScreen({ message, error }: Readonly<{ message: string; error?: boolean }>) {
  return (
    <div className="grid h-screen w-screen place-items-center bg-paper">
      <div className="text-center">
        <img
          src="/assets/Litmus_Logo.png"
          alt="Litmus"
          className="mx-auto h-12 w-12 object-contain"
        />
        <div className="mt-5 font-display text-[24px] font-semibold tracking-tightest text-ink">
          Litmus Catalog Agent
        </div>
        <div className={"mt-2 text-[13px] " + (error ? "text-rose-700" : "text-ash")}>
          {message}
        </div>
      </div>
    </div>
  );
}

function Forbidden() {
  return (
    <div className="mx-auto max-w-lg p-12 text-center">
      <h1 className="font-display text-[28px] font-semibold tracking-tight text-ink">
        Access denied
      </h1>
      <p className="mt-2 text-sm text-ash">Your role does not permit this page.</p>
    </div>
  );
}

// RedirectTo nudges the SPA to a different URL on mount. Used to keep old
// deep-links working after a page move (e.g. /users → /settings#users).
function RedirectTo({ to, setLocation }: { to: string; setLocation: (p: string) => void }) {
  useEffect(() => {
    const [path, hash] = to.split("#");
    // Wouter only owns pathname; set the hash via window.location first so
    // /settings's hash listener picks up the right section on mount.
    if (hash && window.location.hash !== `#${hash}`) {
      window.location.hash = hash;
    }
    setLocation(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function NotFound() {
  return (
    <div className="mx-auto max-w-lg p-12 text-center">
      <h1 className="font-display text-[28px] font-semibold tracking-tight text-ink">Not found</h1>
    </div>
  );
}
